import { sendCommand } from './extension'
import { supabase } from './supabase'
import { createLogger } from './logger'
import type { CrmMapping } from './db'

const log = createLogger('crm')

export type { CrmMapping }

export type CrmName = 'affinity'  // Narrow type — expand when adding new CRMs

export interface AffinityConnection {
  userId: number
  tenantSubdomain: string
  userName: string
  userEmail: string
  status: 'active' | 'invalid'
  connectedAt: number
  verifiedAt: number
  // Added in Phase 3 (field enrichment):
  enrichmentEnabled?: boolean
  enrichmentFieldsSchemaVersion?: number | null
  enrichmentFieldIds?: EnrichmentFieldIds | null
  enrichmentSetupAt?: number | null
}

// =============================================================================
// SCHEMA COUPLING
// =============================================================================
// The EnrichmentFieldKey union and ALL_ENRICHMENT_FIELD_KEYS below MUST mirror
// the `key` values in extension/crm-field-definitions.js (AFFINITY_PERSON_FIELDS).
//
// Any schema change is a TWO-FILE edit:
//   1. extension/crm-field-definitions.js — add/remove/rename the field definition
//   2. this file — update EnrichmentFieldKey and ALL_ENRICHMENT_FIELD_KEYS
//
// Also bump FIELD_SCHEMA_VERSION in crm-field-definitions.js so users' partial
// setup states get re-evaluated correctly.
// =============================================================================

// Known field keys — authoritative list for Phase 4 writes
export type EnrichmentFieldKey =
  | 'lastSent'
  | 'lastSentBy'
  | 'lastReceived'
  | 'lastReceivedBy'
  | 'lastDirection'
  | 'inviteSent'
  | 'inviteSentBy'
  | 'inviteAccepted'
  | 'inviteInviter'
  | 'firstMessage'
  | 'messagesSent'
  | 'messagesReceived'
  | 'lastActivity'
  | 'inviteStatus'

// Partial during setup or after a partial failure; complete when all 14 are present.
export type EnrichmentFieldIds = Partial<Record<EnrichmentFieldKey, number>>

export const ALL_ENRICHMENT_FIELD_KEYS: EnrichmentFieldKey[] = [
  'lastSent', 'lastSentBy', 'lastReceived', 'lastReceivedBy', 'lastDirection',
  'inviteSent', 'inviteSentBy', 'inviteAccepted', 'inviteInviter',
  'firstMessage', 'messagesSent', 'messagesReceived', 'lastActivity', 'inviteStatus',
]

export function isEnrichmentComplete(ids: EnrichmentFieldIds | null): boolean {
  if (!ids) return false
  return ALL_ENRICHMENT_FIELD_KEYS.every((k) => typeof ids[k] === 'number')
}

export function missingEnrichmentKeys(ids: EnrichmentFieldIds | null): EnrichmentFieldKey[] {
  if (!ids) return ALL_ENRICHMENT_FIELD_KEYS.slice()
  return ALL_ENRICHMENT_FIELD_KEYS.filter((k) => typeof ids[k] !== 'number')
}

export interface EnrichmentConfig {
  enabled: boolean
  fieldIds: EnrichmentFieldIds | null
  fieldsSchemaVersion: number | null
  setupAt: number | null
}

export interface CrmConnections {
  affinity: AffinityConnection | null
}

export function getCrmConnections(): Promise<CrmConnections> {
  return sendCommand<CrmConnections>('CRM_GET_CONNECTIONS')
}

export function connectAffinity(apiKey: string) {
  return sendCommand<AffinityConnection>('CRM_CONNECT_AFFINITY', { apiKey })
}

export type CrmTestResult =
  | { ok: true; status: 'active'; verifiedAt: number }
  | { ok: false; status: 'invalid' | 'error' | 'active'; message: string }

export function testCrmConnection(crmName: CrmName) {
  return sendCommand<CrmTestResult>('CRM_TEST_CONNECTION', { crmName })
}

export function disconnectCrm(crmName: CrmName) {
  return sendCommand<{ ok: true }>('CRM_DISCONNECT', { crmName })
}

// --- Contact matching (Phase 2) ---

export interface MatchProfileInput {
  profileUrn: string
  firstName?: string
  lastName?: string
  displayName?: string
  headline?: string         // Used to extract current company
  company?: string          // Optional override if caller already has it
  email?: string
  publicIdentifier?: string // LinkedIn vanity slug — enables name reconstruction
  // Harmonic-enriched fields — enable email-first + historical-company matching
  emails?: string[]         // All known emails (primary first)
  companyDomain?: string    // Current company domain
  experience?: Array<{ companyName: string | null }>  // Historical companies for cross-ref
}

export function matchCrmContact(crmName: CrmName, profile: MatchProfileInput) {
  return sendCommand<CrmMapping>('CRM_MATCH_CONTACT', { crmName, profile })
}

/**
 * Fetch Harmonic-enriched fields from Supabase for CRM matching.
 * Returns emails, company domain, and experience history — or null if
 * the contact hasn't been Harmonic-enriched yet.
 */
export async function fetchHarmonicMatchData(profileUrn: string): Promise<{
  emails: string[]
  companyDomain: string | null
  experience: Array<{ companyName: string | null }>
} | null> {
  try {
    const { data, error } = await supabase
      .from('contacts')
      .select('emails, company_domain, experience')
      .eq('profile_urn', profileUrn)
      .maybeSingle()
    if (error) {
      // Previously we swallowed every error silently, which hid auth-context
      // bleeds (signed-out calls, RLS denials) behind a "not enriched yet"
      // null return. Surface them so /logs filters pick them up; still
      // return null so the CRM matcher falls back to heuristics gracefully.
      log.warn('fetchHarmonicMatchData failed', { profileUrn, code: error.code, message: error.message })
      return null
    }
    if (!data) return null
    // Runtime-guard the JSONB fields. RLS / trigger bugs could in theory hand
    // us a non-array (NULL is fine, already handled; a malformed row might
    // put a string here) — validating element shape prevents a downstream
    // `.map(...)` from blowing up the matcher on bad data.
    const rawEmails = Array.isArray(data.emails) ? data.emails : []
    const emails = rawEmails.filter((e): e is string => typeof e === 'string' && e.length > 0)
    const rawExp = Array.isArray(data.experience) ? data.experience : []
    const experience = rawExp
      .map((row): { companyName: string | null } | null => {
        if (!row || typeof row !== 'object') return null
        const companyName = (row as { companyName?: unknown }).companyName
        return {
          companyName: typeof companyName === 'string' ? companyName : null,
        }
      })
      .filter((row): row is { companyName: string | null } => row !== null)
    return {
      emails,
      companyDomain: data.company_domain || null,
      experience,
    }
  } catch (err) {
    log.warn('fetchHarmonicMatchData threw', { profileUrn, err })
    return null
  }
}

export function getCrmMappingCommand(profileUrn: string, crmName: CrmName) {
  return sendCommand<CrmMapping | null>('CRM_GET_MAPPING', { profileUrn, crmName })
}

export function setCrmMapping(params: {
  profileUrn: string
  crmName: CrmName
  crmRecordId: string
  crmContactName: string
  crmContactEmail?: string | null
}) {
  return sendCommand<CrmMapping>('CRM_SET_MAPPING', params)
}

export function skipCrmMapping(params: {
  profileUrn: string
  crmName: CrmName
  displayName?: string
}) {
  return sendCommand<CrmMapping>('CRM_SKIP_MAPPING', params)
}

// --- Field enrichment (Phase 3) ---

export function setupEnrichment(crmName: CrmName) {
  return sendCommand<{ ok: true; fieldCount: number; fieldIds: EnrichmentFieldIds }>(
    'CRM_ENRICHMENT_SETUP',
    { crmName },
  )
}

export function disableEnrichment(crmName: CrmName) {
  return sendCommand<{ ok: true }>('CRM_ENRICHMENT_DISABLE', { crmName })
}

export function verifyEnrichment(crmName: CrmName) {
  return sendCommand<{
    ok: boolean
    reason?: 'not_enabled' | 'verify_failed' | 'stale_fields' | null
    message?: string
    totalFields?: number
    stale?: Array<{ key: string; id: number }>
  }>('CRM_ENRICHMENT_VERIFY', { crmName })
}

// --- Message sync (Phase 4a) ---

export interface SyncedRecord {
  messageUrn: string
  crmName: CrmName
  interactionId: number
  burstId: string
  syncedAt: number
}

// Aggregate state accumulated across the sync run. The service worker returns
// this on the SyncReport and the web app forwards it to the
// upsert_person_activity RPC — these numbers/dates used to be written into
// 14 Affinity custom fields but live in Supabase now.
export interface SyncRunningTotals {
  sentMessages: number
  receivedMessages: number
  latestTimestamp: number
  latestSentAt: number
  latestSentBy: string | null
  latestReceivedAt: number
  latestReceivedBy: string | null
  latestDirection: 'Sent' | 'Received' | null
  firstMessageAt: number | null
}

export interface SyncReport {
  conversationUrn: string
  crmName: CrmName
  totalMessages: number
  alreadySynced: number
  toSync: number
  bursts: number
  burstsSucceeded: number
  burstsFailed: number
  errors: Array<{ burstIndex: number; messageUrns: string[]; message: string; status?: number }>
  skippedReason: 'enrichment_disabled' | 'conversation_not_found' | 'group_conversation' | 'no_valid_mapping' | null
  // Mirrored back into Dexie by useConversationSync so its useLiveQuery badge
  // notices the new rows — the service worker writes raw IDB and Dexie can't
  // observe cross-context writes on its own.
  syncedRecords: SyncedRecord[]
  // Forwarded to Supabase via upsert_person_activity. Null when the sync
  // skipped before processing bursts (enrichment_disabled, no_valid_mapping,
  // etc.) or when there were no unsynced messages.
  runningTotals: SyncRunningTotals | null
  // Identity fields sourced from the conversation + mapping, passed through
  // so the RPC can populate the person_urn key and display cache without a
  // second Dexie lookup in the web app.
  participantProfileUrn: string | null
  participantName: string | null
  linkedinSlug: string | null
}

// The SW's IDB is in a different origin from Dexie (extension origin vs.
// fwdslash.dev origin). The SW can't read conversations/messages directly, so
// the caller must supply them from Dexie.
export interface SyncConversationPayload {
  crmName: CrmName
  conversationUrn: string
  conversation: {
    conversationUrn: string
    participantProfileUrn: string | null
    participantName?: string
    isGroup: boolean
  }
  messages: Array<{
    messageUrn: string
    conversationUrn: string
    timestamp: number
    isOutgoing: boolean
    text?: string
    senderName?: string
  }>
  // Suppress the SW's CRM_SYNC_COMPLETED broadcast. useConversationSync sets
  // false because it does the Dexie mirror + Supabase upsert inline. Sidepanel
  // direct + SW auto-sync paths leave it default-true so useCrmSyncReconciler
  // picks it up.
  notifyOnComplete?: boolean
}

export function syncConversationToCrm(args: SyncConversationPayload) {
  return sendCommand<SyncReport>('CRM_SYNC_CONVERSATION', args as unknown as Record<string, unknown>)
}

// Broadcast payload from the service worker after a successful conversation
// sync that wasn't initiated by the inline web-app path. Subscribed to in
// useCrmSyncReconciler. Mirrors enough of SyncReport to do the Dexie mirror
// and the Supabase RPC without another round-trip into the SW.
//
// syncId is a UUID per inner sync. The reconciler uses it to dedupe across
// open FwdSlash tabs — chrome.runtime.sendMessage reaches every tab's content
// script, and upsert_person_activity is additive on the SQL side, so without
// idempotency multiple tabs would N-count the totals.
export interface CrmSyncCompletedBroadcast {
  syncId: string
  conversationUrn: string
  crmName: CrmName
  syncedRecords: SyncedRecord[]
  runningTotals: SyncRunningTotals | null
  participantProfileUrn: string | null
  participantName: string | null
  linkedinSlug: string | null
}

export function openCrmSyncReview(input: {
  conversationUrn: string
  profileUrn: string
  displayName?: string | null
  headline?: string | null
  profileImage?: string | null
}) {
  return sendCommand<{ opened: boolean }>(
    'OPEN_CRM_SYNC_REVIEW',
    input as unknown as Record<string, unknown>,
  )
}

// --- Invitation event sync (Phase 4b) ---

export interface InviteBackfillResult {
  ok: boolean
  reason?: 'not_connected' | 'fetch_failed'
  message?: string
  newEvents?: number
  pendingCount?: number
  totalSyncedRecords?: number
}

// Triggers the service worker to walk its current sent-invitations list,
// emit any missing 'sent' / 'accepted' (outbound) events, and write notes to
// Affinity. Cannot backfill 'withdrawn' events (no persisted history) or
// historical 'accepted' events (LinkedIn doesn't expose the acceptance
// moment to the sender).
export function backfillInvitations(crmName: CrmName) {
  return sendCommand<InviteBackfillResult>('CRM_BACKFILL_INVITATIONS', { crmName })
}

// Broadcast payload from the service worker when an invite event syncs.
// Subscribed to in useInviteSync. The `event` field mirrors the crmInviteSync
// row written by the extension, plus enough identity info to call the
// Supabase RPC without another round-trip.
export interface CrmInviteEventBroadcast {
  profileUrn: string
  eventType: 'sent' | 'accepted' | 'withdrawn' | 'declined' | 'expired'
  direction?: 'inbound' | 'outbound'
  crmName: CrmName
  affinityNoteId: number
  syncedAt: number
  eventAt: number
  counterpartyName: string | null
  linkedinSlug: string | null
}
